import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Ban,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Database,
  Eye,
  FileSpreadsheet,
  FileText,
  FileUp,
  ListChecks,
  RefreshCw,
  TriangleAlert,
  LucideLanguages,
} from "lucide-react";
import * as XLSX from "xlsx/xlsx.mjs";
import { importVocabularyExcel, previewVocabularyExcel } from "@api/Vocabulary";
import { EditWordModal } from "@components/Vocabulary/EditWordModal/EditWordModal";
import { PREVIEW_PAGE_SIZE } from "./VocabularyBulkUploadModal.constants";
import { getLangSpanishSimple } from "@components/Vocabulary/PersonalizedTest/functions/functions";
import { capitalizeFirstLetter } from "functions/Functions";

import "./VocabularyBulkUploadModal.scss";
import {
  ImportError, ImportMode, ImportRecord, ImportSummary, ImportSummaryMeta,
  ImportWordSnapshot, NoteColumnKey, PreviewData, RequiredColumnDefinition, VocabularyBulkUploadModalProps
} from "./types/BulkUpload.types";

enum LanguageOptions {
  Inglés = "inglés",
  Francés = "francés",
  Ruso = "ruso",
  Alemán = "alemán",
  Italiano = "italiano",
  Portugués = "portugués",
  Chino = "chino",
  Japonés = "japonés",
}

// Actualización de nombres de cabeceras visibles en la plantilla:
// "palabra en lengua origen" pasa a mostrarse como "palabra en lengua origen (Pinyin/Kana)"
// "caracter especial" pasa a mostrarse como "caracter"
// Se conservan los nombres anteriores como aliases para mantener compatibilidad con archivos antiguos.
// Ahora activamos modo estricto para forzar que se usen únicamente los encabezados nuevos.
const STRICT_CANONICAL_HEADERS = true;
const BASE_REQUIRED_COLUMNS: Array<RequiredColumnDefinition> = [
  { header: "palabra en lengua origen (Pinyin/Kana)", aliases: ["palabra en lengua origen"] },
  { header: "seminario" },
  { header: "traducción español" },
  { header: "categoría" },
  { header: "subcategoría" },
  { header: "idioma", aliases: ["idioma (1)"] },
  { header: "caracter", aliases: ["caracter especial", "en caso de ser chino o japones (caracter especial)"] },
  { header: "aparece en material" },
];

const NOTE_COLUMNS: Array<{ key: NoteColumnKey; label: string; aliases: Array<string> }> = [
  { key: "warning", label: "Advertencia", aliases: ["advertencia"] },
  { key: "memory", label: "Memoria", aliases: ["memoria"] },
  { key: "grammar", label: "Gramática", aliases: ["gramatica", "gramática"] },
  { key: "advice", label: "Consejo", aliases: ["consejo"] },
  { key: "fake_friend", label: "Falso amigo", aliases: ["false_amigo", "falso amigo", "falso_amigo"] },
  { key: "similarity", label: "Similitud", aliases: ["similitud"] },
  { key: "pronunciation", label: "Pronunciación", aliases: ["pronunciacion", "pronunciación"] },
  { key: "did_YouKnow", label: "¿Sabías que?", aliases: ["sabias_que", "sabias que", "sabías que", "¿sabías que?"] },
];

const normalizeHeader = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s?/.-]/g, "")
    .replace(/\s+/g, " ");

const ALL_REQUIRED_COLUMNS = [
  ...BASE_REQUIRED_COLUMNS.map((column) => column.header),
  ...NOTE_COLUMNS.map((column) => column.label),
];

// Mapa de alias -> nombre canónico de visualización (siempre mostrar los nuevos nombres verdes)
const CANONICAL_HEADER_ALIASES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const canonicalPairs: Array<{ canonical: string; aliases: string[] }> = [
    {
      canonical: "palabra en lengua origen (Pinyin/Kana)",
      aliases: ["palabra en lengua origen"],
    },
    {
      canonical: "caracter",
      aliases: [
        "caracter especial",
        "en caso de ser chino o japones (caracter especial)",
      ],
    },
  ];
  canonicalPairs.forEach(({ canonical, aliases }) => {
    const normalizedCanonical = normalizeHeader(canonical);
    map[normalizedCanonical] = canonical; // canónico se mapea a sí mismo
    aliases.forEach((alias) => {
      map[normalizeHeader(alias)] = canonical;
    });
  });
  return map;
})();

const canonicalizeHeaderForDisplay = (label?: string | null) => {
  if (!label) return label ?? "";
  const normalized = normalizeHeader(label);
  return CANONICAL_HEADER_ALIASES[normalized] ?? label;
};

const normalizeLanguage = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const LANGUAGE_LOOKUP: Record<string, LanguageOptions> = (
  Object.values(LanguageOptions) as Array<LanguageOptions>
).reduce((accumulator, option) => {
  accumulator[normalizeLanguage(option)] = option;
  return accumulator;
}, {} as Record<string, LanguageOptions>);

const formatLanguageLabel = (value: LanguageOptions) =>
  value.charAt(0).toUpperCase() + value.slice(1);

const ALLOWED_LANGUAGE_LIST = (
  Object.values(LanguageOptions) as Array<LanguageOptions>
).map(formatLanguageLabel);

const ALLOWED_LANGUAGES_DESCRIPTION = ALLOWED_LANGUAGE_LIST.join(", ");
const NOTE_COLUMNS_DESCRIPTION = NOTE_COLUMNS.map((column) => column.label).join(", ");

export function VocabularyBulkUploadModal({ show, onHide }: VocabularyBulkUploadModalProps) {
  const instructions = useMemo(
    () => [
      {
        icon: <ListChecks size={18} />,
        text: "Mantén las cabeceras originales y usa el tipo correcto: texto para los términos, notas, traducciones, categorías y subcategorías; números enteros del 0 al 60 para seminarios (el 61 se convierte a 0); 'Sí' o 'No' para aparece en material.",
      },
      {
        icon: <Database size={18} />,
        text: "Las traducciones, categorías y subcategorías admiten múltiples valores separados por comas. Si una categoría o subcategoría no existe en la base de datos, se creará automáticamente.",
      },
      {
        icon: <RefreshCw size={18} />,
        text: "El proceso valida cada fila en el servidor; corrige los errores mostrados antes de volver a intentar.",
      },
      {
        icon: <LucideLanguages size={18} />,
        text: `En el campo idioma solo puedes usar: ${ALLOWED_LANGUAGES_DESCRIPTION} (se aceptan variantes sin acentos o mayúsculas/minúsculas).`,
      },
      {
        icon: <FileText size={18} />,
        text: `Las notas se registran en columnas separadas: ${NOTE_COLUMNS_DESCRIPTION}. Déjalas vacías si no cuentas con información.`,
      },
      {
        icon: <BookOpen size={18} />,
        text: "Los términos se asignan a seminarios válidos (números del 0 al 60). Un mismo término puede asignarse a múltiples seminarios sin reemplazar los existentes.",
      },
    ],
    [ALLOWED_LANGUAGES_DESCRIPTION, NOTE_COLUMNS_DESCRIPTION],
  );

  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Array<ImportError>>([]);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [mode, setMode] = useState<ImportMode>("idle");
  const [showDetails, setShowDetails] = useState(true);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [missingColumns, setMissingColumns] = useState<Array<string>>([]);
  const [invalidLanguageErrors, setInvalidLanguageErrors] = useState<Array<{ rowNumber: number; value: string }>>([]);
  const [selectedRecord, setSelectedRecord] = useState<ImportRecord | null>(null);
  const [previewRecords, setPreviewRecords] = useState<Array<ImportRecord>>([]);
  const [previewMeta, setPreviewMeta] = useState<ImportSummaryMeta | null>(null);
  const [hasMoreRecords, setHasMoreRecords] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "created" | "updated">("all");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [editWordModal, setEditWordModal] = useState<{ show: boolean; word_data_id: number | null }>({ show: false, word_data_id: null });
  const [showConfirmImport, setShowConfirmImport] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewBacklogRef = useRef<Array<ImportRecord>>([]);
  const autoPreviewFileRef = useRef<string | null>(null);

  // React Query client para invalidar cache
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!show) {
      resetState();
    }
  }, [show]);

  const hasErrors = errors.length > 0;
  const hasSchemaIssues = missingColumns.length > 0;

  const toggleDetails = useCallback(() => {
    setShowDetails((prev) => !prev);
  }, []);

  const resetState = useCallback(() => {
    setFile(null);
    setErrors([]);
    setImportSummary(null);
    setMode("idle");
    setIsDragging(false);
    setShowDetails(true);
    setPreview(null);
    setMissingColumns([]);
    setInvalidLanguageErrors([]);
    setSelectedRecord(null);
    setPreviewRecords([]);
    setPreviewMeta(null);
    setHasMoreRecords(false);
    setIsLoadingMore(false);
    setEditWordModal({ show: false, word_data_id: null });
    previewBacklogRef.current = [];
  }, []);

  const handleClose = useCallback(
    (force?: boolean | React.MouseEvent<HTMLElement>) => {
      const shouldForce = typeof force === "boolean" ? force : false;
      if (isSubmitting && !shouldForce) {
        return;
      }
      onHide();
      resetState();
    },
    [isSubmitting, onHide, resetState],
  );

  const isPreviewMode = mode === "preview";
  const isCompletedImport = mode === "completed";
  const buildPreview = useCallback(
    async (nextFile: File) => {
      try {
        const buffer = await nextFile.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        if (!worksheet || !worksheet["!ref"]) {
          setPreview(null);
          setMissingColumns([...ALL_REQUIRED_COLUMNS]);
          setInvalidLanguageErrors([]);
          return;
        }

        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        const headerRows = XLSX.utils.sheet_to_json<Array<any>>(worksheet, {
          header: 1,
          range: { s: { r: range.s.r, c: range.s.c }, e: { r: range.s.r, c: range.e.c } },
          blankrows: false,
        });
        const rawColumns = headerRows[0] ?? [];
        const cleanedHeaders = rawColumns.map((column) =>
          column !== undefined && column !== null ? String(column).trim() : "",
        );
        const columns = cleanedHeaders.filter((column) => column.length > 0);

        const normalizedColumns = columns.map((column) => normalizeHeader(column));
        const normalizedIdioma = normalizeHeader("idioma");
        const allowedNormalizedColumns = new Set<string>();

        BASE_REQUIRED_COLUMNS.forEach(({ header, aliases }) => {
          const normalizedHeader = normalizeHeader(header);
          allowedNormalizedColumns.add(normalizedHeader);
          if (!STRICT_CANONICAL_HEADERS) {
            (aliases ?? []).forEach((alias) => allowedNormalizedColumns.add(normalizeHeader(alias)));
          }
        });

        NOTE_COLUMNS.forEach((noteColumn) => {
          allowedNormalizedColumns.add(normalizeHeader(noteColumn.label));
          noteColumn.aliases.forEach((alias) => allowedNormalizedColumns.add(normalizeHeader(alias)));
        });

        const missingBase = BASE_REQUIRED_COLUMNS.filter(({ header, aliases }) => {
          if (STRICT_CANONICAL_HEADERS) {
            const normalizedHeader = normalizeHeader(header);
            return !normalizedColumns.includes(normalizedHeader);
          }
          const candidates = [header, ...(aliases ?? [])].map((value) => normalizeHeader(value));
            return !normalizedColumns.some((value) => candidates.includes(value));
        }).map(({ header }) => canonicalizeHeaderForDisplay(header));

        const noteMissing: Array<string> = [];
        NOTE_COLUMNS.forEach((noteColumn) => {
          const normalizedAliases = noteColumn.aliases.map((alias) => normalizeHeader(alias));
            const hasColumn = normalizedColumns.some((value) => normalizedAliases.includes(value));
            if (!hasColumn) {
              noteMissing.push(canonicalizeHeaderForDisplay(noteColumn.label));
            }
        });

        setMissingColumns([...missingBase, ...noteMissing]);


        const idiomaDefinition = BASE_REQUIRED_COLUMNS.find(
          ({ header }) => normalizeHeader(header) === normalizedIdioma,
        );
        const languageCandidates = idiomaDefinition
          ? [idiomaDefinition.header, ...(idiomaDefinition.aliases ?? [])]
          : ["idioma"];
        const normalizedLanguageCandidates = languageCandidates.map((candidate) => normalizeHeader(candidate));
        const languageHeaderIndex = normalizedColumns.findIndex((header) =>
          normalizedLanguageCandidates.includes(header),
        );
        const languageColumnLabel = languageHeaderIndex >= 0 ? columns[languageHeaderIndex] : null;

        const extraColumns = normalizedColumns
          .map((header, index) => ({ header, label: columns[index] }))
          .filter(({ header }) => header.length > 0 && !allowedNormalizedColumns.has(header));

        const dataRows: Array<Record<string, string>> =
          columns.length > 0
            ? XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, {
              header: columns,
              range: {
                s: { r: range.s.r + 1, c: range.s.c },
                e: { r: range.e.r, c: range.e.c },
              },
              blankrows: false,
              defval: "",
              raw: false,
            })
            : [];

        const totalRows = dataRows.length;

        let sampleRows: Array<Record<string, string>> = [];

        if (dataRows.length > 0) {
          sampleRows = dataRows.slice(0, 5).map((row) => ({ ...row }));

          if (languageColumnLabel) {
            sampleRows = sampleRows.map((row) => {
              const rawValue = String(row[languageColumnLabel] ?? "");
              if (!rawValue.trim()) {
                return row;
              }
              const formatted = rawValue
                .split(",")
                .map((part) => part.trim())
                .filter((part) => part.length > 0)
                .map((part) => {
                  const normalized = normalizeLanguage(part);
                  const matched = LANGUAGE_LOOKUP[normalized];
                  return matched ? formatLanguageLabel(matched) : part;
                })
                .join(", ");

              return {
                ...row,
                [languageColumnLabel]: formatted,
              };
            });
          }
        }

        if (languageColumnLabel && dataRows.length > 0) {
          const invalids: Array<{ rowNumber: number; value: string }> = [];
          dataRows.forEach((row, index) => {
            const rawValue = String(row[languageColumnLabel] ?? "").trim();
            if (rawValue.length === 0) {
              invalids.push({ rowNumber: range.s.r + 2 + index, value: "(vacío)" });
              return;
            }

            const languages = rawValue
              .split(",")
              .map((part) => part.trim())
              .filter((part) => part.length > 0);

            if (languages.length === 0) {
              invalids.push({ rowNumber: range.s.r + 2 + index, value: "(vacío)" });
              return;
            }

            const invalidValues = languages.filter((language) => {
              const normalized = normalizeLanguage(language);
              return !LANGUAGE_LOOKUP[normalized];
            });

            if (invalidValues.length > 0) {
              invalidValues.forEach((invalid) => {
                invalids.push({ rowNumber: range.s.r + 2 + index, value: invalid });
              });
            }
          });

          setInvalidLanguageErrors(invalids);
        } else {
          setInvalidLanguageErrors([]);
        }

        const fileSize = (() => {
          if (nextFile.size < 1024) {
            return `${nextFile.size} B`;
          }
          if (nextFile.size < 1024 * 1024) {
            return `${(nextFile.size / 1024).toFixed(1)} KB`;
          }
          return `${(nextFile.size / (1024 * 1024)).toFixed(1)} MB`;
        })();

        setPreview({
          fileName: nextFile.name,
          fileSize,
          sheetName: sheetName ?? "Hoja 1",
          totalRows,
          columns,
          sampleRows,
          extraColumns,
        });
      } catch (error) {
        console.error(error);
        toast.error("No se pudo generar la vista previa del archivo.");
        setPreview(null);
        setMissingColumns(ALL_REQUIRED_COLUMNS);
        setInvalidLanguageErrors([]);
  // legacy header warnings removidos (se integran implícitamente en los paneles existentes)
      }
    },
    [],
  );

  const handleFileSelection = useCallback(
    (nextFile: File | null) => {
      setErrors([]);
      setImportSummary(null);
      setMode("idle");
      setPreview(null);
      setMissingColumns([]);
      setInvalidLanguageErrors([]);
      setSelectedRecord(null);
      setPreviewRecords([]);
      setPreviewMeta(null);
      setHasMoreRecords(false);
      setIsLoadingMore(false);
      previewBacklogRef.current = [];
      autoPreviewFileRef.current = null;
      setFile(nextFile);
      if (nextFile) {
        buildPreview(nextFile);
      }
    },
    [buildPreview],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      const droppedFiles = event.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        handleFileSelection(droppedFiles[0]);
      }
    },
    [handleFileSelection],
  );

  const applyPreviewSummary = useCallback(
    (summary: ImportSummary, options?: { append?: boolean }) => {
      const append = options?.append ?? false;
      const incomingRecords = summary?.records ?? [];
      const incomingErrors = summary?.errors ?? [];
      const effectivePageSize = Math.max(
        1,
        Math.min(PREVIEW_PAGE_SIZE, summary?.meta?.pageSize ?? PREVIEW_PAGE_SIZE),
      );

      const pageRecords = incomingRecords.slice(0, effectivePageSize);
      const leftoverRecords = incomingRecords.slice(effectivePageSize);
      previewBacklogRef.current = append
        ? [...previewBacklogRef.current, ...leftoverRecords]
        : leftoverRecords;
      const baseRecords = append ? previewRecords : [];
      const mergedRecords = append ? [...baseRecords, ...pageRecords] : pageRecords;

      const previousTotal = append ? previewMeta?.totalRecords ?? previewRecords.length : 0;
      const backendTotalRecords = summary?.meta?.totalRecords;
      const fallbackFromIncoming = append
        ? previousTotal > 0
          ? Math.max(previousTotal, mergedRecords.length)
          : previewRecords.length + incomingRecords.length
        : incomingRecords.length;
      const inferredTotalRecords = Math.max(
        backendTotalRecords ?? 0,
        mergedRecords.length,
        fallbackFromIncoming,
      );

      const nextPage = summary?.meta?.page
        ? summary.meta.page
        : append && previewMeta
          ? previewMeta.page + 1
          : mergedRecords.length > 0
            ? 1
            : 0;

      const nextTotalPages = summary?.meta?.totalPages
        ? summary.meta.totalPages
        : inferredTotalRecords > 0
          ? Math.max(1, Math.ceil(inferredTotalRecords / effectivePageSize))
          : 0;

      const derivedMeta: ImportSummaryMeta = {
        page: Math.min(nextPage, nextTotalPages === 0 ? 0 : nextTotalPages),
        pageSize: effectivePageSize,
        totalRecords: inferredTotalRecords,
        totalPages: nextTotalPages,
      };

      setPreviewRecords(mergedRecords);
      setErrors(incomingErrors);
      setPreviewMeta(derivedMeta);
      const hasMore = derivedMeta.totalRecords > mergedRecords.length || derivedMeta.page < derivedMeta.totalPages;
      setHasMoreRecords(hasMore);
      setImportSummary({
        created: summary?.created ?? importSummary?.created ?? 0,
        updated: summary?.updated ?? importSummary?.updated ?? 0,
        records: mergedRecords,
        errors: incomingErrors,
        meta: derivedMeta,
      });
    },
    [importSummary, PREVIEW_PAGE_SIZE, previewMeta, previewRecords],
  );

  const runPreview = useCallback(async () => {
    if (!file) {
      toast.error("Selecciona un archivo Excel antes de continuar.");
      return;
    }

    if (hasSchemaIssues) {
      toast.error("Corrige las columnas requeridas antes de importar.");
      return;
    }

    if (invalidLanguageErrors.length > 0) {
      toast.error(`Corrige los idiomas inválidos antes de importar (permitidos: ${ALLOWED_LANGUAGES_DESCRIPTION}).`);
      setShowDetails(true);
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors([]);

      const formData = new FormData();
      formData.append("file", file);

      const summary: ImportSummary = await previewVocabularyExcel(formData, {
        page: 1,
        pageSize: PREVIEW_PAGE_SIZE,
      });

      applyPreviewSummary(summary);
      setShowDetails(true);
      setMode("preview");
      setSelectedRecord(null);

      if ((summary?.errors ?? []).length > 0) {
        toast.error("Se detectaron errores en la vista previa. Corrige el archivo y vuelve a intentarlo.");
      }
    } catch (error: any) {
      console.error(error);
      const apiMessage = error?.response?.data?.message ?? "No fue posible generar la vista previa.";
      toast.error(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [applyPreviewSummary, file, hasSchemaIssues, invalidLanguageErrors.length]);

  // Ejecutar vista previa automáticamente cuando el archivo está listo y sin problemas de esquema/idiomas
  useEffect(() => {
    if (!file) return;
    if (mode !== 'idle') return; // ya se movió a preview/completed antes
    if (!preview) return; // aún construyendo preview local
    if (hasSchemaIssues) return;
    if (invalidLanguageErrors.length > 0) return;
    if (autoPreviewFileRef.current === file.name) return; // ya lanzado para este archivo
    autoPreviewFileRef.current = file.name;
    void runPreview();
  }, [file, preview, mode, hasSchemaIssues, invalidLanguageErrors.length, runPreview]);

  // Descargar plantilla base
  const handleDownloadTemplate = useCallback(() => {
    try {
      const headers = [
        ...BASE_REQUIRED_COLUMNS.map(({ header }) => header),
        ...NOTE_COLUMNS.map(({ label }) => label),
      ];
      const worksheet = XLSX.utils.aoa_to_sheet([headers]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, worksheet, "Vocabulario");
      const fileBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([fileBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'vocabulary-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error('No se pudo descargar la plantilla.');
    }
  }, []);

  // Importar definitivamente (se llama tras confirmar)
  const runImport = useCallback(async () => {
    if (!file) {
      toast.error('No hay archivo seleccionado.');
      return;
    }
    if (errors.length > 0) {
      toast.error('Corrige los errores antes de importar.');
      return;
    }
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('file', file);
      const summary: ImportSummary = await importVocabularyExcel(formData);
      const created = summary?.created ?? 0;
      const updated = summary?.updated ?? 0;
      const records = summary?.records ?? [];
      const importErrors = summary?.errors ?? [];
      const meta = summary?.meta ?? {
        page: records.length > 0 ? 1 : 0,
        pageSize: records.length,
        totalRecords: records.length,
        totalPages: records.length > 0 ? 1 : 0,
      };
      setErrors(importErrors);
      setPreviewRecords(records);
      setPreviewMeta(meta);
      setHasMoreRecords(false);
      previewBacklogRef.current = [];
      setImportSummary({ created, updated, records, errors: importErrors, meta });
      setMode('completed');
      setShowConfirmImport(false);

      // Invalida caches relevantes
      await queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
      await queryClient.invalidateQueries({ queryKey: ['categories'] });
      await queryClient.invalidateQueries({ queryKey: ['subcategories'] });
      await queryClient.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes('seminary') });

      if (importErrors.length > 0) {
        toast.error(`Importación con observaciones: ${created} nuevas · ${updated} actualizadas · ${importErrors.length} errores.`);
      } else {
        toast.success(`Importación exitosa: ${created} nuevas · ${updated} actualizadas.`);
      }
    } catch (e: any) {
      console.error(e);
      const apiMessage = e?.response?.data?.message ?? 'No fue posible completar la importación.';
      toast.error(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [errors.length, file, queryClient]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore) {
      return;
    }

    if (previewBacklogRef.current.length > 0) {
      setIsLoadingMore(true);
      try {
        const chunk = previewBacklogRef.current.slice(0, PREVIEW_PAGE_SIZE);
        previewBacklogRef.current = previewBacklogRef.current.slice(chunk.length);

        const fallbackTotalRecords =
          previewMeta?.totalRecords ?? (previewRecords.length + chunk.length + previewBacklogRef.current.length);
        const syntheticSummary: ImportSummary = {
          created: importSummary?.created ?? 0,
          updated: importSummary?.updated ?? 0,
          records: chunk,
          errors: importSummary?.errors ?? [],
          meta: {
            page: (previewMeta?.page ?? 1) + 1,
            pageSize: PREVIEW_PAGE_SIZE,
            totalRecords: fallbackTotalRecords,
            totalPages:
              previewMeta?.totalPages ??
              (fallbackTotalRecords > 0
                ? Math.max(1, Math.ceil(fallbackTotalRecords / PREVIEW_PAGE_SIZE))
                : 0),
          },
        };

        applyPreviewSummary(syntheticSummary, { append: true });
      } finally {
        setIsLoadingMore(false);
      }
      return;
    }

    if (!file || !previewMeta) {
      return;
    }

    const nextPage = previewMeta.page + 1;
    if (previewMeta.totalPages === 0 || nextPage > previewMeta.totalPages) {
      return;
    }

    try {
      setIsLoadingMore(true);
      const formData = new FormData();
      formData.append("file", file);
      const summary: ImportSummary = await previewVocabularyExcel(formData, {
        page: nextPage,
        pageSize: previewMeta.pageSize || PREVIEW_PAGE_SIZE,
      });

      applyPreviewSummary(summary, { append: true });
    } catch (error) {
      console.error(error);
      toast.error("No se pudo cargar más resultados.");
    } finally {
      setIsLoadingMore(false);
    }
  }, [applyPreviewSummary, file, importSummary, isLoadingMore, previewMeta, previewRecords]);

  const handlePrimaryAction = useCallback(() => {
    if (mode === "completed") {
      resetState();
      const input = fileInputRef.current;
      if (input) {
        input.value = "";
        input.click();
      }
      return;
    }
    if (mode === "preview") {
      setShowConfirmImport(true);
      return;
    }
  }, [mode, resetState]);

  const formatList = useCallback((values: Array<string | number>) => {
    if (!values || values.length === 0) return "—";
    return values
      .map(v => typeof v === 'number' ? v.toString() : v)
      .filter(v => v.length > 0)
      .join(', ');
  }, []);

  const formatSeminariesList = useCallback((values: Array<string | number>) => {
    if (!values || values.length === 0) return "—";
    return values
      .map(value => {
        const numValue = typeof value === 'number' ? value : parseInt(value);
        return numValue === 61 ? '0' : (typeof value === 'number' ? value.toString() : value);
      })
      .filter(v => v.length > 0)
      .join(', ');
  }, []);

  const formatLanguageDisplay = useCallback((language: string) => {
    if (!language) return "—";
    const normalized = language.trim().toLowerCase();
    const lang = getLangSpanishSimple(normalized);
    if (!lang) return "—";
    return capitalizeFirstLetter(lang);
  }, []);

  const formatNotes = useCallback(
    (snapshot?: ImportWordSnapshot) => {
      if (!snapshot || !snapshot.notes) {
        return [] as Array<{ key: NoteColumnKey; label: string; value: string }>;
      }
      return NOTE_COLUMNS.filter(({ key }) => Boolean(snapshot.notes?.[key]))
        .map(({ key, label }) => ({ key, label, value: snapshot.notes?.[key] ?? "" }));
    },
    [],
  );

  // Función para mejorar los mensajes de error
  const improveErrorMessage = useCallback((message: string, column?: string) => {
    // Mejorar mensaje específico para seminarios
    if (message.includes("Los seminarios deben ser números enteros válidos") || 
        (column && column.toLowerCase().includes("seminario") && message.includes("número"))) {
      return "Los seminarios deben ser números enteros válidos del 1 al 60";
    }
    
    // Agregar más mejoras de mensajes aquí si es necesario
    return message;
  }, []);

  const handleOpenRecord = useCallback((record: ImportRecord) => {
    setEditWordModal({ show: true, word_data_id: record.wordDataId });
  }, []);

  const handleCloseEditWordModal = useCallback(() => {
    setEditWordModal({ show: false, word_data_id: null });
  }, []);



  const isPrimaryDisabled = useMemo(() => {
    if (mode === 'preview') {
      return isSubmitting || errors.length > 0;
    }
    if (mode === 'completed') {
      return isSubmitting; // mientras procesa (raro) se desactiva
    }
    return true; // idle
  }, [mode, isSubmitting, errors.length]);

  const cardVariant = useMemo(() => {
    if (isSubmitting) {
      return "info";
    }
    if (hasSchemaIssues || invalidLanguageErrors.length > 0) {
      return "error";
    }
    if (isCompletedImport) {
      return "success";
    }
    if (isPreviewMode && !hasErrors) {
      return "success";
    }
    if (hasErrors) {
      return "error";
    }
    if (file) {
      return "info";
    }
    return "muted";
  }, [file, hasErrors, hasSchemaIssues, invalidLanguageErrors.length, isCompletedImport, isPreviewMode, isSubmitting]);

  const cardIcon = useMemo(() => {
    if (isSubmitting || isLoadingMore) {
      return <RefreshCw size={18} className="spin" />;
    }
    if (hasSchemaIssues || invalidLanguageErrors.length > 0) {
      return <TriangleAlert size={18} />;
    }
    if (isCompletedImport) {
      return <CheckCircle2 size={18} />;
    }
    if (isPreviewMode) {
      if (hasErrors) {
        return <TriangleAlert size={18} />;
      }
      return <Eye size={18} />;
    }
    if (!file) {
      return <FileSpreadsheet size={18} />;
    }
    if (hasErrors) {
      return <TriangleAlert size={18} />;
    }
    return <Eye size={18} />;
  }, [file, hasErrors, hasSchemaIssues, invalidLanguageErrors.length, isCompletedImport, isPreviewMode]);

  const cardTitle = useMemo(() => {
    if (!file) {
      return isSubmitting ? "Generando vista previa..." : "Vista previa pendiente";
    }
    if (hasSchemaIssues) {
      return "Formato inválido";
    }
    if (invalidLanguageErrors.length > 0) {
      return "Idiomas inválidos detectados";
    }
    if (isCompletedImport) {
      return `Importación completada (${importSummary?.created ?? 0} nuevas · ${importSummary?.updated ?? 0} actualizadas)`;
    }
    if (isPreviewMode) {
      if (hasErrors) {
        return `${errors.length} fila${errors.length === 1 ? "" : "s"} con observaciones`;
      }
      return `Vista previa lista (${importSummary?.created ?? 0} nuevas · ${importSummary?.updated ?? 0} actualizadas)`;
    }
    if (hasErrors) {
      return `${errors.length} fila${errors.length === 1 ? "" : "s"} con observaciones`;
    }
    return "Archivo listo para generar vista previa";
  }, [errors.length, file, hasErrors, hasSchemaIssues, importSummary, invalidLanguageErrors.length, isCompletedImport, isPreviewMode]);

  const cardSubtitle = useMemo(() => {
    if (!file) {
      return "Selecciona un archivo para comenzar";
    }
    if (hasSchemaIssues) {
      return `Faltan ${missingColumns.length} columna${missingColumns.length === 1 ? "" : "s"} requerida${missingColumns.length === 1 ? "" : "s"}. Revisa la plantilla antes de proceder.`;
    }
    if (invalidLanguageErrors.length > 0) {
      return "Corrige los nombres de idioma antes de continuar.";
    }
    if (isCompletedImport) {
      return `${importSummary?.created ?? 0} nuevas · ${importSummary?.updated ?? 0} actualizadas`;
    }
    if (isPreviewMode) {
      if (hasErrors) {
        return "Corrige las filas con observaciones antes de confirmar la importación.";
      }
      return "Revisa la vista previa y confirma para aplicar los cambios.";
    }
    if (hasErrors) {
      return `${importSummary?.created ?? 0} nuevas · ${importSummary?.updated ?? 0} actualizadas`;
    }
    if (isSubmitting) {
      return "Procesando archivo...";
    }
    return preview?.fileName ?? file.name;
  }, [file, hasErrors, hasSchemaIssues, importSummary, invalidLanguageErrors.length, isCompletedImport, isPreviewMode, isSubmitting, missingColumns.length, preview]);

  const primaryButtonLabel = useMemo(() => {
    if (mode === "completed") {
      return "Subir otro archivo";
    }
    if (mode === "preview") {
      return isSubmitting ? "Importando..." : "Confirmar importación";
    }
    return ""; // oculto en idle (vista previa se genera automáticamente)
  }, [isSubmitting, mode]);

  const allRecords = importSummary?.records ?? previewRecords;
  const displayedRecords = useMemo(() => {
    if (activeTab === "all") return allRecords;
    return allRecords.filter(record => record.action === activeTab);
  }, [allRecords, activeTab]);

  const recordCounts = useMemo(() => {
    const created = allRecords.filter(record => record.action === "created").length;
    const updated = allRecords.filter(record => record.action === "updated").length;
    return { all: allRecords.length, created, updated };
  }, [allRecords]);

  // Índice activo para animación del indicador de tabs
  const activeIndex = useMemo(() => (activeTab === 'all' ? 0 : activeTab === 'created' ? 1 : 2), [activeTab]);

  // Estilos inline para los tabs (evita depender del SCSS)
  const TABS_TOTAL = 3;
  const tabsContainerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    gap: 6,
    marginBottom: 18,
    background: '#f5f6f8',
    padding: 6,
    borderRadius: 14,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    isolation: 'isolate',
  };
  const sliderStyle: React.CSSProperties = {
    position: 'absolute',
    top: 6,
    left: 6,
    width: `calc((100% - ${6 * 2}px) / ${TABS_TOTAL})`,
    height: `calc(100% - ${6 * 2}px)`,
    background: '#ffffff',
    borderRadius: 10,
    boxShadow: '0 2px 4px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)',
    transition: 'transform .35s cubic-bezier(.4,0,.2,1)',
    transform: `translateX(${activeIndex * 100}%)`,
    willChange: 'transform',
    zIndex: 0,
  };
  const basePillStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '10px 14px',
    fontSize: 14,
    fontWeight: 600,
    color: '#586069',
    background: 'transparent',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    letterSpacing: '.2px',
    zIndex: 1,
    transition: 'color .25s ease, transform .25s ease',
    WebkitTapHighlightColor: 'transparent',
  };
  const badgeBase: React.CSSProperties = {
    background: '#e5e7eb',
    color: '#374151',
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    lineHeight: 1.2,
    minWidth: 22,
    textAlign: 'center' as const,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background .25s ease, color .25s ease',
  };
  const getPillStyles = (type: 'all' | 'created' | 'updated'): { button: React.CSSProperties; badge: React.CSSProperties } => {
    const active = activeTab === type;
    const palette: Record<string, { activeColor: string; badgeBg: string; badgeColor: string }> = {
      all: { activeColor: '#0d47a1', badgeBg: 'rgba(13,71,161,.12)', badgeColor: '#0d47a1' },
      created: { activeColor: '#1b7f38', badgeBg: 'rgba(27,127,56,.15)', badgeColor: '#1b7f38' },
      updated: { activeColor: '#a86000', badgeBg: 'rgba(168,96,0,.18)', badgeColor: '#a86000' },
    };
    const paletteItem = palette[type];
    return {
      button: {
        ...basePillStyle,
        color: active ? paletteItem.activeColor : basePillStyle.color,
      },
      badge: {
        ...badgeBase,
        background: active ? paletteItem.badgeBg : badgeBase.background,
        color: active ? paletteItem.badgeColor : badgeBase.color,
      },
    };
  };

  return (
    <>
      <Modal
        show={show && !showConfirmImport}
        onHide={handleClose}
        size="lg"
        aria-labelledby="bulk-upload-modal"
        centered
        className="vocabulary-bulk-upload-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="bulk-upload-modal">Subir vocabulario vía Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={`preview-toggle-card ${cardVariant}`}>
            <div className="info">
              <span className="icon">{cardIcon}</span>
              <div>
                <p className="title">{cardTitle}</p>
                <p className="subtitle">{cardSubtitle}</p>
              </div>
            </div>
            <button type="button" className="toggle-btn" onClick={toggleDetails}>
              {showDetails ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              {showDetails ? "Ocultar" : "Mostrar"}
            </button>
          </div>

          {showDetails && (
            <>
              {preview && (
                <div className="preview-section">
                  <div className="preview-header">
                    <h5>
                      <Eye size={18} /> Vista previa del archivo
                    </h5>
                    <p>
                      Hoja: <strong>{preview.sheetName}</strong> · Tamaño: <strong>{preview.fileSize}</strong>
                    </p>
                  </div>
                  <div className="preview-summary">
                    <div className="summary-card">
                      <span className="summary-icon primary">
                        <FileText size={18} />
                      </span>
                      <p className="value">{preview.totalRows}</p>
                      <p className="label">Filas detectadas</p>
                    </div>
                    {preview.columns.length > 0 && (
                      <div className="summary-card">
                        <span className="summary-icon secondary">
                          <Database size={18} />
                        </span>
                        <p className="value">{preview.columns.length}</p>
                        <p className="label">Columnas</p>
                      </div>
                    )}
                    <div className="summary-card">
                      <span className="summary-icon tertiary">
                        <CalendarDays size={18} />
                      </span>
                      <p className="value">{preview.fileSize}</p>
                      <p className="label">Peso del archivo</p>
                    </div>
                  </div>
                  {preview.columns.length > 0 && (
                    <div className="detected-columns">
                      <h6>Columnas detectadas</h6>
                      <div className="tags">
                        {preview.columns.map((column, index) => (
                          <span className="tag" key={`preview-column-${index}`}>
                            {column}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="upload-area-container">
                <button type="button" className="template-download" onClick={handleDownloadTemplate}>
                  <FileSpreadsheet size={18} /> Descargar plantilla en blanco
                </button>
                <div
                  className={`upload-dropzone${isDragging ? " is-dragging" : ""}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(event) => handleFileSelection(event.target.files?.[0] ?? null)}
                    ref={fileInputRef}
                  />
                  <div className="upload-placeholder">
                    <div className="icon">
                      <FileUp size={24} />
                    </div>
                    <p className="title">Selecciona o arrastra tu archivo Excel</p>
                    <p className="subtitle">Formatos soportados: .xlsx, .xls</p>
                    {file && <p className="file-name">{file.name}</p>}
                  </div>
                </div>
                <div className="upload-hint">
                  <ul>
                    {instructions.map(({ icon, text }, index) => (
                      <li key={`upload-hint-${index}`}>
                        <span className="hint-icon">{icon}</span>
                        <span>{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {hasSchemaIssues && (
                <div className="errors-container warning">
                  <h5>
                    <Ban size={18} /> Columnas requeridas faltantes
                  </h5>
                  <div className="missing-columns">
                    {missingColumns.map((column, index) => (
                      <span className="chip" key={`missing-${index}`}>
                        {column}
                      </span>
                    ))}
                  </div>
                  <p className="inline-hint">Corrige el archivo antes de continuar. No se permitirá la importación.</p>
                </div>
              )}


              {invalidLanguageErrors.length > 0 && (
                <div className="errors-container warning">
                  <h5>
                    <Ban size={18} /> Idiomas inválidos detectados
                  </h5>
                  <p className="inline-hint">Revisa los valores en la columna "idioma". Los idiomas permitidos son: {ALLOWED_LANGUAGES_DESCRIPTION}.</p>
                  <div className="missing-columns">
                    {invalidLanguageErrors.map(({ rowNumber, value }, index) => (
                      <span className="chip" key={`invalid-lang-${index}`}>
                        Fila {rowNumber}: {value}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {mode !== "idle" && importSummary && (
                <div className="success-container">
                  <div className="success-header">
                    {isCompletedImport ? (
                      <CheckCircle2 size={18} />
                    ) : hasErrors ? (
                      <TriangleAlert size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                    <h5>
                      {isCompletedImport
                        ? "Importación completada"
                        : hasErrors
                          ? "Vista previa con observaciones"
                          : "Vista previa generada"}
                    </h5>
                  </div>
                  <div className="success-summary">
                    <div className="summary-chip">
                      <span className="count">{importSummary.created}</span>
                      <span className="label">Nuevas</span>
                    </div>
                    <div className="summary-chip">
                      <span className="count">{importSummary.updated}</span>
                      <span className="label">Sobrescritas</span>
                    </div>
                  </div>
                </div>
              )}

              {allRecords.length > 0 && (
                <div className="import-results">
                  <div className="import-results__header">
                    <h5>Palabras procesadas</h5>
                  </div>

                  {/* Tabs siempre visibles aunque el filtro esté vacío */}
                  <div style={tabsContainerStyle} role="tablist" aria-label="Filtrar palabras importadas">
                    <span aria-hidden="true" style={sliderStyle} />
                    {(['all','created','updated'] as const).map(key => {
                      const styles = getPillStyles(key);
                      const labels: Record<typeof key, { text: string; aria: string; count: number }> = {
                        all: { text: 'Todas', aria: 'Total de palabras', count: recordCounts.all },
                        created: { text: 'Nuevas', aria: 'Palabras nuevas', count: recordCounts.created },
                        updated: { text: 'A Actualizar', aria: 'Palabras a actualizar', count: recordCounts.updated },
                      };
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setActiveTab(key)}
                          style={styles.button}
                          aria-selected={activeTab === key}
                          role="tab"
                        >
                          <span style={{ pointerEvents: 'none' }}>{labels[key].text}</span>
                          <span style={styles.badge} aria-label={labels[key].aria}>{labels[key].count}</span>
                        </button>
                      );
                    })}
                  </div>

                  {displayedRecords.length > 0 ? (
                    <div className="import-results__list">
                      {displayedRecords.map((record) => {
                        const { newData, previousData } = record;
                        const notesEntries = formatNotes(newData);
                        return (
                          <div className={`import-result-card import-result-card--${record.action}`} key={`${record.rowNumber}-${newData.wordDataId}`}>
                            <div className="import-result-card__header">
                              <div className={`import-result-card__badge import-result-card__badge--${record.action}`}>
                                {record.action === "created" ? "Nueva" : "A actualizar"}
                              </div>
                              <div className="import-result-card__title">
                                <span className="import-result-card__word">
                                  {newData.nativeWord}
                                  {newData.optionalWord ? ` (${newData.optionalWord})` : ""}
                                </span>
                                <span className="import-result-card__meta">Fila {record.rowNumber}</span>
                              </div>
                              {record.action === "updated" && previousData && (
                                <button
                                  type="button"
                                  className="import-result-card__action"
                                  onClick={() => handleOpenRecord(record)}
                                >
                                  <Eye size={16} /> Ver información actual
                                </button>
                              )}
                            </div>
                            <div className="import-result-card__content">
                              <div className="import-result-card__grid">
                                <div>
                                  <span className="label">Idioma</span>
                                  <span className="value">{formatLanguageDisplay(newData.language)}</span>
                                </div>
                                <div>
                                  <span className="label">Traducciones</span>
                                  <span className="value">{formatList(newData.translations)}</span>
                                </div>
                                <div>
                                  <span className="label">Categorías</span>
                                  <span className="value">{formatList(newData.categories)}</span>
                                </div>
                                <div>
                                  <span className="label">Subcategorías</span>
                                  <span className="value">{formatList(newData.subcategories)}</span>
                                </div>
                                <div>
                                  <span className="label">Seminarios</span>
                                  <span className="value">{formatSeminariesList(newData.seminaries)}</span>
                                </div>
                                <div>
                                  <span className="label">Aparece en material</span>
                                  <span className="value">{newData.appearsInMaterial ? "Sí" : "No"}</span>
                                </div>
                              </div>
                              {notesEntries.length > 0 && (
                                <div className="import-result-card__notes">
                                  <span className="label">Notas</span>
                                  <div className="notes-list">
                                    {notesEntries.map(({ key, label, value }) => (
                                      <span className="note-chip" key={key}>
                                        <strong>{label}:</strong> {value}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="import-results__empty">
                      <p className="empty-title">
                        {activeTab === 'all' && 'No hay registros procesados aún.'}
                        {activeTab === 'created' && 'No se detectaron palabras nuevas en esta vista.'}
                        {activeTab === 'updated' && 'No se detectaron palabras sobrescritas en esta vista.'}
                      </p>
                      {mode === 'preview' && hasMoreRecords && (
                        <button
                          type="button"
                          className="import-results__load-more-btn"
                          onClick={() => void handleLoadMore()}
                          disabled={isLoadingMore}
                        >
                          {isLoadingMore ? (
                            <>
                              <RefreshCw size={16} className="spin" /> Cargando más...
                            </>
                          ) : (
                            <>Cargar más filas</>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {mode === "preview" && hasMoreRecords && displayedRecords.length > 0 && activeTab === 'all' && (
                    <div className="import-results__load-more">
                      <button
                        type="button"
                        className="import-results__load-more-btn"
                        onClick={() => void handleLoadMore()}
                        disabled={isLoadingMore}
                      >
                        {isLoadingMore ? (
                          <>
                            <RefreshCw size={16} className="spin" /> Cargando...
                          </>
                        ) : (
                          <>Ver más</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {preview?.extraColumns.length ? (
                <div className="info-banner">
                  <h5>
                    <Database size={18} /> Columnas adicionales detectadas
                  </h5>
                  <p className="description">
                    Estas columnas no forman parte del esquema estándar y se ignorarán durante la importación. Mueve su contenido a una columna válida o elimínalas si no
                    son necesarias.
                  </p>
                  <div className="extra-columns">
                    {preview.extraColumns.map(({ label }, index) => (
                      <span className="pill" key={`extra-${index}`}>
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {hasErrors && (
                <div className="errors-container">
                  <h5>
                    <TriangleAlert size={18} /> Errores detectados
                  </h5>
                  <div className="error-list">
                    {errors.map((error, index) => (
                      <div className="error-item" key={`import-error-${index}`}>
                        <div className="badge">{error.rowNumber ?? "—"}</div>
                        <div className="error-content">
                          <p className="message">{improveErrorMessage(error.message, error.column)}</p>
                          {(error.column || (error.value && error.value.length > 0)) && (
                            <div className="error-meta">
                              {error.column && <span className="meta-chip">{error.column}</span>}
                              {error.value && error.value.length > 0 && (
                                <span className="meta-chip meta-chip-muted">Valor: {error.value}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="footer-actions">
            <button type="button" className="secondary-action" onClick={handleClose} disabled={isSubmitting}>
              {mode === "completed" ? "Cerrar" : "Cancelar"}
            </button>
            {mode !== 'idle' && (
              <button
                type="button"
                className="primary-action"
                onClick={handlePrimaryAction}
                disabled={isPrimaryDisabled}
              >
                {primaryButtonLabel}
              </button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
      {showConfirmImport && importSummary && (
        <Modal
          show={showConfirmImport}
          onHide={() => setShowConfirmImport(false)}
          centered
          size="lg"
          aria-labelledby="confirm-import-modal"
          dialogClassName="confirm-import-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title id="confirm-import-modal">Confirmar importación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ marginBottom: 12 }}>
              Se aplicarán los cambios del archivo procesado. ¿Deseas continuar?
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'flex', gap: 16 }}>
              <li style={{ flex: 1, background: '#f1f5f9', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 12, letterSpacing: '.5px', textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>Nuevas</div>
                <div style={{ fontSize: 24, fontWeight: 600, color: '#047857' }}>{importSummary.created}</div>
              </li>
              <li style={{ flex: 1, background: '#f1f5f9', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 12, letterSpacing: '.5px', textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>A actualizar</div>
                <div style={{ fontSize: 24, fontWeight: 600, color: '#b45309' }}>{importSummary.updated}</div>
              </li>
            </ul>
            {errors.length > 0 && (
              <div style={{ background: '#fff7ed', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#92400e', marginBottom: 12 }}>
                Hay <strong>{errors.length}</strong> fila{errors.length === 1 ? '' : 's'} con errores. Debes corregirlas antes de importar.
              </div>
            )}
            <div style={{ fontSize: 13, color: '#64748b' }}>
              Esta acción agregará términos y actualizará las existentes. No eliminará vocablos existentes.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              onClick={() => setShowConfirmImport(false)}
              style={{
                background: 'transparent',
                border: '1px solid #cbd5e1',
                padding: '10px 18px',
                borderRadius: 8,
                fontWeight: 600,
                color: '#475569'
              }}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => { if (!errors.length) { void runImport(); } else { toast.error('Corrige los errores antes de importar.'); } }}
              style={{
                background: 'linear-gradient(90deg,#be123c,#7f1d1d)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 8,
                fontWeight: 600,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                opacity: isSubmitting ? .7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Importando...' : 'Confirmar'}
            </button>
          </Modal.Footer>
        </Modal>
      )}
      <EditWordModal
        readOnly={true}
        show={editWordModal.show}
        onHide={handleCloseEditWordModal}
        word_data_id={editWordModal.word_data_id}
      />
    </>
  );
}

export default VocabularyBulkUploadModal;
